import { DomainError } from '../errors.js';
import type { FileKind } from './file-types.js';

/**
 * Doküman kütüphanesinin yapı kuralları (ADR 0023, ADR 0027; `dokuman-kutuphanesi.md` §1–§3,
 * §5, §8). Saf fonksiyonlar; yazma yolu doğrulayıcıları ve arayüz aynı kuralları kullanır.
 */

/** Kütüphanenin bölümleri. `pms_instructions` M7'de açıldı (ADR 0029 K16, K38). */
export const DOCUMENT_SECTIONS = ['ism', 'manuals', 'pms_instructions'] as const;
export type DocumentSection = (typeof DOCUMENT_SECTIONS)[number];

/**
 * Kullanıma açık bölümler. `pms_instructions` M7'de açıldı: iş tanımının talimatı bu bölümdedir
 * (ADR 0029 K16, K38).
 */
export const ENABLED_DOCUMENT_SECTIONS: readonly DocumentSection[] = [
  'ism',
  'manuals',
  'pms_instructions',
];

export function assertSectionEnabled(section: DocumentSection): void {
  if (!ENABLED_DOCUMENT_SECTIONS.includes(section)) {
    throw new DomainError('SECTION_NOT_ENABLED', section);
  }
}

/** Form gemide doldurulur ve kayıt üretir; prosedür ve manual salt okunurdur (§2). */
export const DOCUMENT_KINDS = ['form', 'procedure', 'manual'] as const;
export type DocumentKind = (typeof DOCUMENT_KINDS)[number];

/** Bölümde bulunabilecek türler: Manuals salt okunurdur, form olmaz (§5). */
export const KINDS_BY_SECTION: Readonly<Record<DocumentSection, readonly DocumentKind[]>> = {
  ism: ['form', 'procedure', 'manual'],
  manuals: ['procedure', 'manual'],
  pms_instructions: ['procedure', 'manual'],
};

export function assertKindAllowedInSection(section: DocumentSection, kind: DocumentKind): void {
  if (!KINDS_BY_SECTION[section].includes(kind)) {
    throw new DomainError('KIND_NOT_ALLOWED_IN_SECTION', `${section}/${kind}`);
  }
}

/** Kapsam: bütün gemiler, seçili gemiler ya da yalnız ofis (§2). */
export const DOCUMENT_SCOPES = ['fleet', 'vessels', 'office'] as const;
export type DocumentScope = (typeof DOCUMENT_SCOPES)[number];

/** Doküman bu gemide görünür mü (gemide ekran ve dağıtım bu kuralı kullanır). */
export function documentVisibleOn(
  document: { scope: DocumentScope },
  scopeVesselIds: readonly string[],
  vesselId: string,
): boolean {
  switch (document.scope) {
    case 'fleet':
      return true;
    case 'vessels':
      return scopeVesselIds.includes(vesselId);
    case 'office':
      return false;
  }
}

export interface FolderNode {
  id: string;
  parentId: string | null;
  section: DocumentSection;
  code: string;
}

/**
 * Klasörün yeni yeri ve kodu geçerli mi (ekleme, taşıma, yeniden adlandırma):
 * - üst klasör var ve aynı bölümde (`FOLDER_SECTION_MISMATCH`);
 * - döngü yok: klasör kendi altına taşınamaz (`FOLDER_CYCLE`);
 * - aynı üst klasörde (kökte de) kod tekildir (`DUPLICATE_FOLDER_CODE`).
 * @param folders Bölümdeki silinmemiş klasörler (klasörün kendi eski hali dahil olabilir).
 */
export function validateFolderMove(folders: readonly FolderNode[], folder: FolderNode): void {
  const byId = new Map(folders.map((f) => [f.id, f]));
  if (folder.parentId !== null) {
    const parent = byId.get(folder.parentId);
    if (!parent && folder.parentId !== folder.id) {
      throw new DomainError('NOT_FOUND', `folder ${folder.parentId}`);
    }
    if (parent && parent.section !== folder.section) {
      throw new DomainError('FOLDER_SECTION_MISMATCH', folder.parentId);
    }
    // Yeni üstten köke yürü: klasörün kendisine rastlanırsa döngü olur
    const seen = new Set<string>();
    let cursor: string | null = folder.parentId;
    while (cursor !== null) {
      if (cursor === folder.id) throw new DomainError('FOLDER_CYCLE', folder.id);
      if (seen.has(cursor)) throw new DomainError('FOLDER_CYCLE', cursor);
      seen.add(cursor);
      cursor = byId.get(cursor)?.parentId ?? null;
    }
  }
  const code = folder.code.trim().toLocaleLowerCase('en');
  const duplicate = folders.find(
    (f) =>
      f.id !== folder.id &&
      f.section === folder.section &&
      f.parentId === folder.parentId &&
      f.code.trim().toLocaleLowerCase('en') === code,
  );
  if (duplicate) throw new DomainError('DUPLICATE_FOLDER_CODE', folder.code);
}

/** Dal türleri: ana dal bütün kapsam içindir; varyant seçili gemiler içindir (ADR 0023 §4). */
export const BRANCH_KINDS = ['main', 'variant'] as const;
export type BranchKind = (typeof BRANCH_KINDS)[number];

/** Varyant dalları M8c'ye kadar kapalıdır [sorular #21]. */
export const VARIANT_BRANCHES_ENABLED = false;

/** Yeni dal açılabilir mi: tek ana dal; varyant M8c'ye kadar reddedilir. */
export function assertBranchAllowed(
  existing: readonly { kind: BranchKind }[],
  kind: BranchKind,
): void {
  if (kind === 'variant' && !VARIANT_BRANCHES_ENABLED) {
    throw new DomainError('VARIANT_BRANCHES_NOT_ENABLED');
  }
  if (kind === 'main' && existing.some((branch) => branch.kind === 'main')) {
    throw new DomainError('MAIN_BRANCH_EXISTS');
  }
}

/**
 * Kişisel "Read & Understood" varsayılanı (ADR 0028 #29): prosedürde okuma gerekir; form ve
 * manualde gerekmez. Yükleyen değiştirebilir.
 */
export function defaultReadRequired(kind: DocumentKind): boolean {
  return kind === 'procedure';
}

/** Revizyon ekinin rolü: ek ya da onaylı baskı PDF'i (ADR 0028 #34). */
export const ATTACHMENT_ROLES = ['attachment', 'print_rendition'] as const;
export type AttachmentRole = (typeof ATTACHMENT_ROLES)[number];

/**
 * Baskı PDF'i kuralları (ADR 0028 #34): yalnız ana dosyası DOCX ya da XLSX olan revizyona
 * (`RENDITION_NOT_APPLICABLE`), yalnız PDF (`RENDITION_MUST_BE_PDF`), revizyon başına bir tane
 * (`RENDITION_EXISTS`). Taslak kuralı (`REVISION_LOCKED`) ekin genel kuralıdır.
 */
export function assertRenditionAllowed(
  mainKind: FileKind,
  renditionKind: FileKind,
  existingRoles: readonly AttachmentRole[],
): void {
  if (mainKind !== 'docx' && mainKind !== 'xlsx') {
    throw new DomainError('RENDITION_NOT_APPLICABLE', mainKind);
  }
  if (renditionKind !== 'pdf') throw new DomainError('RENDITION_MUST_BE_PDF', renditionKind);
  if (existingRoles.includes('print_rendition')) throw new DomainError('RENDITION_EXISTS');
}

/** Onaylanan içeriğin girdisi: revizyon satırının değiştirilemez alanları. */
export interface RevisionContentInput {
  docNoSnapshot: string;
  titleSnapshot: string;
  revisionNo: string;
  changeSummary: string;
  fileSha256: string;
  fileSize: number;
  fileKind: FileKind;
  /** Geri getirilen içeriğin kaynağı (Reinstate; ADR 0028 #26). */
  reinstatedFromRevisionId: string | null;
  /** Kişisel okuma gerekir mi (ADR 0028 #29); kitle içeriğe girmez. */
  readRequired: boolean;
}

/**
 * Onaylanacak içerik (mimari belge §7.3): doküman no ve başlığın anlık kopyası, revizyon no,
 * değişiklik özeti, ana dosyanın SHA-256'sı, boyutu ve türü, eklerin SHA-256'ları (sıralı),
 * baskı PDF'inin SHA-256'sı, okuma gereği ve geri getirilen kaynak. Ek sırası içeriği
 * değiştirmez. Hash'i `@felix/sync` `contentHash` hesaplar; onay bu hash'i taşır ve içerik
 * değişirse geçersizdir.
 */
export function revisionContent(
  revision: RevisionContentInput,
  attachments: readonly { sha256: string; role: AttachmentRole }[],
) {
  const rendition = attachments.find((attachment) => attachment.role === 'print_rendition');
  return {
    docNo: revision.docNoSnapshot,
    title: revision.titleSnapshot,
    revisionNo: revision.revisionNo,
    changeSummary: revision.changeSummary,
    file: { sha256: revision.fileSha256, size: revision.fileSize, kind: revision.fileKind },
    attachments: attachments
      .filter((attachment) => attachment.role === 'attachment')
      .map((attachment) => attachment.sha256)
      .sort(),
    printRenditionSha256: rendition?.sha256 ?? null,
    readRequired: revision.readRequired,
    reinstatedFromRevisionId: revision.reinstatedFromRevisionId,
  };
}
export type RevisionContent = ReturnType<typeof revisionContent>;
